/* Behaviour for components that need it. Everything else in the design system
   is CSS -- the React originals used state for hover and focus, which :hover
   and :focus-within do natively.

   One delegated listener on the document rather than one per instance, so a
   page with a stepper in every row of a cart costs the same as a page with one,
   and steppers added after load still work without re-binding. */
(() => {
  'use strict';

  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-qty-step]');
    if (!button) return;

    const group = button.closest('[data-qty]');
    if (!group) return;

    const display = group.querySelector('[data-qty-display]');
    const input = group.querySelector('[data-qty-input]');
    const min = Number(group.dataset.min || 1);
    const max = Number(group.dataset.max || 99);

    // The field holds packs; the display may show pieces. Reading the
    // display back would read "150 cases" as NaN, so the field is the source.
    const current = Number(input ? input.value : display.textContent) || min;
    const next = clamp(current + Number(button.dataset.qtyStep), min, max);
    if (next === current) return;

    const per = Number(group.dataset.per || 1);
    display.textContent = per > 1 ? (next * per) + ' ' + (group.dataset.noun || '') : next;

    // A pack-counted row keeps the buy panel's headline at the order total.
    // The totals arrive formatted by Liquid, one per permitted count from the
    // floor, so the currency is the shopper's rather than assumed here.
    if (group.dataset.totals) {
      try {
        const total = JSON.parse(group.dataset.totals)[next - min];
        const panel = group.closest('[data-buy]');
        const head = panel && panel.querySelector('[data-buy-price]');
        if (head && total) head.textContent = total;
      } catch (err) {
        // The count still changes; only the headline stays behind. Loud
        // rather than silent, since this is a bug in the rendered data.
        console.warn('lucullion: unreadable wholesale totals', err);
      }
    }
    if (input) {
      input.value = next;
      // Anything listening for a quantity change -- a cart line, a price
      // recalculation -- hears this. Setting .value alone fires nothing.
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Disable at the ends rather than letting the click do nothing. A control
    // that silently ignores you reads as broken.
    group.querySelectorAll('[data-qty-step]').forEach((btn) => {
      const step = Number(btn.dataset.qtyStep);
      btn.disabled = (step < 0 && next <= min) || (step > 0 && next >= max);
    });
  });
})();

/* Product gallery. Clicking a thumbnail swaps the main image in place rather
   than navigating, and aria-current moves with it so the strip announces which
   one is showing as well as outlining it.

   Delegated from the document, like the quantity stepper: any number of
   galleries on a page cost one listener rather than one each. */
document.addEventListener('click', function (e) {
  var thumb = e.target.closest('[data-gallery-thumb]');
  if (!thumb) return;
  var gallery = thumb.closest('[data-gallery]');
  /* Found by class rather than a data attribute: image_tag silently drops any
     parameter whose name contains a hyphen, so data-gallery-main never reached
     the markup. */
  var main = gallery && gallery.querySelector('.lu-gallery__main img');
  if (!main) return;

  main.src = thumb.dataset.full;
  /* The main image is responsive, so a stale srcset would outrank the src we
     just set and leave the old picture on screen. */
  main.removeAttribute('srcset');
  main.alt = thumb.dataset.alt || '';

  gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (t) {
    t.setAttribute('aria-current', String(t === thumb));
  });
});

/* Variant chips. Each option group reports its position, so a selection is read
   by collecting the pressed chip from every group in order and finding the
   variant whose options array matches. That works for one option or three
   without the script knowing which product it is on.

   The URL gains ?variant= so the choice survives a reload, a share, or a back
   button -- replaceState rather than pushState, because picking a size is not a
   place in history you want to press back through. */
/* Two shapes of control, one mechanism. A chip is a toggle and carries
   aria-pressed; a volume ladder row is a radio and carries aria-checked. Which
   attribute an element already has decides which one gets written, so neither
   ends up with the other's semantics. */
var LU_OPTION_CONTROL = '.lu-tag, .lu-tier-row';

document.addEventListener('click', function (e) {
  var chip = e.target.closest('[data-option-group] .lu-tag, [data-option-group] .lu-tier-row');
  if (!chip) return;

  var panel = chip.closest('[data-buy]');
  var data = panel && panel.querySelector('[data-variants]');
  if (!data) return;

  var group = chip.closest('[data-option-group]');
  group.querySelectorAll(LU_OPTION_CONTROL).forEach(function (t) {
    var on = t === chip;
    if (t.hasAttribute('aria-checked')) t.setAttribute('aria-checked', String(on));
    else t.setAttribute('aria-pressed', String(on));
    if (t.classList.contains('lu-tier-row')) t.classList.toggle('lu-tier-row--on', on);
    else t.classList.toggle('lu-tag--accent', on);
  });

  var chosen = Array.prototype.map.call(
    panel.querySelectorAll('[data-option-group]'),
    function (g) {
      var on = g.querySelector('[aria-pressed="true"], [aria-checked="true"]');
      return on ? on.value : null;
    }
  );

  var variants = JSON.parse(data.textContent);
  var match = variants.find(function (v) {
    return v.options.every(function (o, i) { return o === chosen[i]; });
  });
  if (!match) return;

  panel.querySelector('[data-variant-id]').value = match.id;

  var price = panel.querySelector('[data-buy-price]');
  if (price) price.textContent = match.price;

  var unit = panel.querySelector('[data-buy-unit]');
  if (unit && match.unit) unit.textContent = match.unit;

  /* Wholesale rows are bought in packs from a floor, and the stepper follows
     the row: packs counted, cases shown, starting at the minimum. Any other
     row puts it back to ones, 1 to 99. Switching resets to the row's floor
     rather than carrying a count across -- 7 singles are not 7 packs of 50. */
  var qty = panel.querySelector('[data-qty]');
  if (qty && typeof match.min === 'number') {
    var qMin = match.ws ? match.min : 1;
    var qMax = match.ws ? match.max : 99;
    var qPer = match.ws ? match.pack : 1;
    qty.dataset.min = qMin;
    qty.dataset.max = qMax;
    qty.dataset.per = qPer;
    qty.dataset.totals = match.ws && match.totals ? JSON.stringify(match.totals) : '';
    var qInput = qty.querySelector('[data-qty-input]');
    var qShow = qty.querySelector('[data-qty-display]');
    if (qInput) qInput.value = qMin;
    if (qShow) qShow.textContent = qPer > 1 ? (qMin * qPer) + ' ' + (qty.dataset.noun || '') : qMin;
    /* The count was just reset to the floor, so minus is always at its end
       here; plus is only at its end when the floor is also the stock. */
    qty.querySelectorAll('[data-qty-step]').forEach(function (b) {
      var st = Number(b.dataset.qtyStep);
      b.disabled = st < 0 || qMin >= qMax;
    });
    /* The price line above was set to one pack's price like any other row;
       on a wholesale row it becomes the total at the floor. */
    if (match.ws && match.totals && match.totals[0] && price) price.textContent = match.totals[0];
  }

  var add = panel.querySelector('[data-add]');
  if (add) {
    add.disabled = !match.available;
    var label = add.querySelector('[data-add-label]');
    if (label) label.textContent = match.available ? 'Add to cart' : 'Sold out';
  }

  luSyncPicker(panel, match.options);
  luSyncMessage(panel, match.options);

  var url = new URL(window.location.href);
  url.searchParams.set('variant', match.id);
  window.history.replaceState({}, '', url);
});

/* Cart. The page works without any of this: the quantity fields are named
   updates[key], Remove is a link to Shopify's own change URL, and there is a
   submit button behind <noscript>. What follows replaces the page reload, not
   the mechanism.

   Changes go through /cart/change.js with a sections parameter, so Shopify
   returns this section re-rendered from the updated cart rather than the script
   patching numbers it computed itself. Subtotals, discounts and the free
   shipping line all come back correct without the browser knowing any of the
   pricing rules. */
(function () {
  'use strict';

  var section = function () { return document.querySelector('[data-cart-root]'); };

  function apply(state) {
    var root = section();
    if (!root || !state.sections) return;

    var id = root.dataset.cartRoot;
    var html = state.sections[id];
    if (html) {
      var fresh = new DOMParser().parseFromString(html, 'text/html')
        .querySelector('[data-cart-root]');
      if (fresh) root.replaceWith(fresh);
    }

    /* The header is not re-rendered for the sake of one number. */
    var badge = document.querySelector('.lu-header__badge');
    var icon = document.querySelector('.lu-header__icon[href*="cart"]');
    if (icon) {
      icon.setAttribute('aria-label', state.item_count > 0
        ? 'Cart, ' + state.item_count + ' items'
        : 'Cart, empty');
    }
    if (badge) {
      badge.textContent = state.item_count;
      badge.hidden = state.item_count === 0;
    }
  }

  function change(line, quantity) {
    var root = section();
    if (!root) return;
    root.querySelector('.lu-cart').setAttribute('aria-busy', 'true');

    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: line,
        quantity: quantity,
        sections: root.dataset.cartRoot,
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json(); })
      .then(apply)
      /* A cart that silently fails to update is worse than one that reloads.
         Falling back to the plain form submission means the change still
         happens. */
      .catch(function () { window.location.reload(); });
  }

  function lineOf(el) {
    var li = el.closest('.lu-line');
    return li ? Array.prototype.indexOf.call(li.parentElement.children, li) + 1 : 0;
  }

  document.addEventListener('change', function (e) {
    if (!e.target.matches('[data-qty-input]')) return;
    if (!e.target.closest('[data-cart-root]')) return;
    change(lineOf(e.target), Number(e.target.value));
  });

  document.addEventListener('click', function (e) {
    var remove = e.target.closest('[data-cart-remove]');
    if (!remove) return;
    e.preventDefault();
    change(lineOf(remove), 0);
  });
})();

/* Forms that submit themselves when a control inside them changes -- the
   collection sort, and any facet form once storefront filters are configured.

   The form is a real GET form either way, with a submit button behind
   <noscript>. This only removes the second click. */
document.addEventListener('change', function (e) {
  var form = e.target.closest('form[data-autosubmit]');
  if (!form) return;
  if (typeof form.requestSubmit === 'function') form.requestSubmit();
  else form.submit();
});

/* FAQ filtering.

   The chip row is rendered hidden and this reveals it, so a browser without
   JavaScript is never shown a control that does nothing -- every group is
   visible in that case, which is the "All" state the page starts in anyway.

   Filtering changes how many groups are on screen, so the ground has to be
   restriped afterwards. The rule is the one in lucullion-faq.liquid: alternate,
   except the last visible group is always white, because the footer below it is
   grey and two greys must never meet. */
(function () {
  'use strict';

  var row = document.querySelector('[data-faq-filters]');
  if (!row) return;
  row.hidden = false;

  var groups = Array.prototype.slice.call(document.querySelectorAll('[data-faq-group]'));

  function stripe() {
    var visible = groups.filter(function (g) { return !g.hidden; });
    visible.forEach(function (g, i) {
      var last = i === visible.length - 1;
      g.classList.toggle('lu-section--sunken', !last && i % 2 === 1);
    });
  }

  row.addEventListener('click', function (e) {
    var chip = e.target.closest('.lu-tag');
    if (!chip) return;

    row.querySelectorAll('.lu-tag').forEach(function (t) {
      var on = t === chip;
      t.setAttribute('aria-pressed', String(on));
      t.classList.toggle('lu-tag--accent', on);
    });

    var want = chip.value;
    groups.forEach(function (g) {
      g.hidden = want !== 'all' && g.dataset.faqGroup !== want;
    });
    stripe();
  });
})();

/* Development board filtering.

   Same arrangement as the FAQ: the chip row is rendered hidden and revealed
   here, so a browser without JavaScript is never shown a control that does
   nothing -- it gets every project, which is the "All" state the page starts
   in.

   Chips carry their test in the value: "all", "vertical:Collecting" or
   "stage:Shipped". The board reads it rather than the label, so a chip renamed
   in the editor keeps working. */
(function () {
  'use strict';

  var row = document.querySelector('[data-board-filters]');
  if (!row) return;
  row.hidden = false;

  var items = Array.prototype.slice.call(document.querySelectorAll('[data-board-item]'));
  var none = document.querySelector('[data-board-none]');

  row.addEventListener('click', function (e) {
    var chip = e.target.closest('.lu-chip');
    if (!chip) return;

    row.querySelectorAll('.lu-chip').forEach(function (c) {
      c.setAttribute('aria-pressed', String(c === chip));
    });

    var parts = chip.value.split(':');
    var field = parts[0];
    var want = parts.slice(1).join(':');
    var shown = 0;

    items.forEach(function (item) {
      var hit = field === 'all' || item.dataset[field] === want;
      item.hidden = !hit;
      if (hit) shown++;
    });

    /* An empty stage is information, not a gap -- so the board says which
       filter emptied it rather than going silently blank. */
    if (none) none.hidden = shown > 0;
  });
})();

/* Private sender: recommend a coverage tier from the declared value.

   The kit derives coverage silently. Here the customer is charged for it, so
   the select is real and this only recommends -- it moves the select and says
   why, in front of them rather than behind them.

   Everything on the page works with this script absent: the select is a
   variant picker, the routes are radios switched by CSS, and the consent box
   is required. */
(function () {
  'use strict';

  var data = document.querySelector('[data-sender-variants]');
  var value = document.getElementById('ps-value');
  var select = document.getElementById('ps-coverage');
  if (!data || !value || !select) return;

  var cfg = JSON.parse(data.textContent);
  var byTitle = {};
  cfg.variants.forEach(function (v) { byTitle[v.title] = v; });

  var note = document.createElement('p');
  note.className = 'lu-sender__tier';
  note.setAttribute('role', 'status');
  value.closest('.lu-field').insertAdjacentElement('afterend', note);

  /* Only ever formats the difference between two variants, which the store
     already priced in whole cents. */
  function money(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function summarize() {
    var picked = cfg.variants.filter(function (v) { return String(v.id) === select.value; })[0];
    if (!picked) return;
    var label = document.querySelector('[data-sender-coverage-label]');
    var extra = document.querySelector('[data-sender-coverage]');
    var total = document.querySelector('[data-sender-total]');
    if (label) label.textContent = picked.title;
    if (extra) extra.textContent = picked.extra > 0 ? money(picked.extra) : 'Included';
    if (total) total.textContent = picked.money;
  }

  function recommend() {
    var n = parseFloat(String(value.value).replace(/[^0-9.]/g, ''));
    if (!isFinite(n) || n <= 0) {
      note.textContent = '';
      note.className = 'lu-sender__tier';
      return;
    }

    var tier = cfg.ceilings.filter(function (t) { return n <= parseFloat(t.ceiling); })[0];
    if (!tier) {
      note.textContent = cfg.overLimitNote;
      note.className = 'lu-sender__tier lu-sender__tier--over';
      return;
    }

    var variant = byTitle[tier.variant];
    if (variant && String(variant.id) !== select.value) {
      select.value = String(variant.id);
      summarize();
    }
    if (variant && variant.extra > 0) {
      /* The variant's own title, not the raw ceiling. The ceiling is a number
         for comparing against; the title is what the customer just saw in the
         select, and "coverage to 1000" beside an option reading "Up to $1,000"
         reads as two different things. */
      note.textContent = 'Declared at that value, coverage ' +
        variant.title.charAt(0).toLowerCase() + variant.title.slice(1) +
        ' is selected, which adds ' + money(variant.extra) + '.';
      note.className = 'lu-sender__tier lu-sender__tier--raise';
    } else {
      note.textContent = 'Covered up to the standard ceiling, included.';
      note.className = 'lu-sender__tier';
    }
  }

  value.addEventListener('input', recommend);
  value.addEventListener('blur', recommend);
  select.addEventListener('change', summarize);
})();

/* Intake slip.

   The reference is the order number and arrives in the query string, because it
   does not exist until checkout completes and Liquid cannot read a query string
   anyway. So the page is sent showing the message, and the slip replaces it only
   once a reference is in hand.

   That ordering is the safety property: a browser with no JavaScript, or a link
   that lost its reference, gets the sentence explaining how to get one -- never
   a slip. A printed slip carrying no real reference goes into a box, arrives
   with a parcel, and matches no order. */
(function () {
  'use strict';

  var slip = document.querySelector('[data-slip]');
  var ref = document.querySelector('[data-slip-ref]');
  if (!slip || !ref) return;

  var code = '';
  try {
    code = (new URLSearchParams(window.location.search).get('ref') || '').trim();
  } catch (e) {
    code = '';
  }

  /* Bounded and character-checked before it is drawn. It is printed onto paper
     that goes to a person, and an order number is letters, digits and dashes --
     anything else arrived from somewhere other than our confirmation email. */
  if (!code || code.length > 32 || !/^[A-Za-z0-9#-]+$/.test(code)) return;

  ref.textContent = code;
  slip.hidden = false;

  var none = document.querySelector('[data-slip-none]');
  if (none) none.hidden = true;

  var intro = document.querySelector('[data-slip-intro]');
  if (intro && intro.dataset.withRef) intro.textContent = intro.dataset.withRef;

  /* Only a real reference gets a print button. */
  var printer = document.querySelector('[data-slip-print]');
  if (printer) {
    printer.hidden = false;
    printer.addEventListener('click', function (e) {
      if (e.target.closest('button')) window.print();
    });
  }
})();

/* The vertical notify band asks a second question -- which craft, what you
   build, what you are trying to source -- and the answer becomes a tag on the
   subscriber rather than a message nobody can query.

   Shopify's customer form keeps the email, the name and the tags. Tags are the
   only part of it that can be asked a question later, and "who asked for
   leatherworking" is exactly that question, so the answer is merged into the
   same comma-separated field the newsletter tag rides in. */
(function () {
  'use strict';

  document.addEventListener('submit', function (e) {
    var form = e.target;
    var tags = form.querySelector('[data-capture-tags]');
    var second = form.querySelector('[name="lu_second"]');
    if (!tags || !second) return;

    /* Commas separate tags, so one inside an answer would split it into two.
       Bounded as well: a tag is a label, not a paragraph. */
    var answer = String(second.value || '').replace(/,/g, ' ').trim().slice(0, 60);
    tags.value = answer ? 'newsletter, ' + answer : 'newsletter';

    /* Named fields post whether or not Shopify reads them, and an unrecognised
       one on a customer form is noise in the request. */
    second.removeAttribute('name');
  });
})();

/* Marketplace marks come from four different brand programmes at four very
   different proportions: TikTok Shop's wordmark is 8.4:1 where Etsy's is 2:1.
   Sized to a common height, TikTok would run wider than its tile; sized to a
   common width it would be a sliver. Each is solved to the same optical area
   instead, which is what lets four unrelated marks read as one row.

   Only the width is set. The height follows from the mark's own proportions,
   so the area comes out right without the two ever disagreeing. */
var LU_LOGO_AREA = 5200;

function luSizeMarketplaceLogo(img) {
  var w = img.naturalWidth;
  var h = img.naturalHeight;
  if (!w || !h) return;
  img.style.width = Math.sqrt(LU_LOGO_AREA * (w / h)).toFixed(1) + 'px';
}

document.querySelectorAll('[data-mkt-logo]').forEach(function (img) {
  if (img.complete) luSizeMarketplaceLogo(img);
  else img.addEventListener('load', function () { luSizeMarketplaceLogo(img); }, { once: true });
});

/* Build-your-own picker.

   Shopify prices by variant, so "any 25 of 75" cannot be a variant -- 75 choose
   25 is a number with 19 digits. The variant carries the price difference and
   the choice rides along as a line item property, which is what reaches the
   order.

   The hidden input stays disabled until the picker is in play. A disabled field
   is not submitted, so the property cannot attach itself to a collection the
   customer did not build. */
/* Whether the variant currently selected can actually be bought, read from the
   same JSON the option controls use rather than from the button's own state --
   which this function is about to overwrite. */
function luVariantAvailable(panel) {
  var data = panel && panel.querySelector('[data-variants]');
  var id = panel && panel.querySelector('[data-variant-id]');
  if (!data || !id) return true;
  var v = JSON.parse(data.textContent).find(function (x) {
    return String(x.id) === String(id.value);
  });
  return v ? v.available !== false : true;
}

function luPickerState(picker) {
  var chips = picker.querySelectorAll('[data-picker-chip]');
  var chosen = [];
  chips.forEach(function (c) {
    if (c.getAttribute('aria-pressed') === 'true') chosen.push(c.value);
  });
  return chosen;
}

function luPickerPaint(picker) {
  var limit = Number(picker.dataset.pickerLimit) || 25;
  var chosen = luPickerState(picker);
  var full = chosen.length >= limit;

  var count = picker.querySelector('[data-picker-count]');
  if (count) {
    count.textContent = chosen.length + ' of ' + limit + ' chosen';
    count.classList.toggle('lu-picker__count--full', full);
  }

  var clear = picker.querySelector('[data-picker-clear]');
  if (clear) clear.hidden = chosen.length === 0;

  /* At the cap the unpicked go disabled rather than disappearing, so the grid
     does not reflow under the cursor. */
  picker.querySelectorAll('[data-picker-chip]').forEach(function (c) {
    var on = c.getAttribute('aria-pressed') === 'true';
    c.disabled = full && !on;
    c.title = on ? 'Remove ' + c.value : full ? limit + ' already chosen' : 'Add ' + c.value;
  });

  picker.querySelectorAll('[data-picker-group-count]').forEach(function (el) {
    var group = el.closest('.lu-picker__group');
    var n = 0;
    group.querySelectorAll('[data-picker-chip][aria-pressed="true"]').forEach(function () { n++; });
    el.textContent = n;
  });

  var input = picker.querySelector('[data-picker-input]');
  if (input) input.value = chosen.join(', ');

  /* The buy button says what is still needed rather than sitting dead. Being
     short of the limit disables it; so does the variant being sold out, and the
     stock answer wins -- completing the picker must not talk the button into
     offering something there is none of. */
  var panel = picker.closest('[data-buy]');
  var add = panel && panel.querySelector('[data-add]');
  if (add && !picker.hidden) {
    var label = add.querySelector('[data-add-label]');
    var short = limit - chosen.length;
    var sellable = luVariantAvailable(panel);
    add.disabled = short > 0 || !sellable;
    if (label) {
      label.textContent = !sellable ? 'Sold out'
        : short > 0 ? 'Choose ' + short + ' more'
        : 'Add to cart';
    }
  }
}

/* Shown only when the chosen variant is the build-your-own one. */
function luSyncPicker(panel, options) {
  var picker = panel && panel.querySelector('[data-picker]');
  if (!picker) return;
  var wanted = picker.dataset.pickerFor;
  var on = Array.isArray(options) && options.indexOf(wanted) !== -1;

  picker.hidden = !on;
  var input = picker.querySelector('[data-picker-input]');
  if (input) input.disabled = !on;

  if (on) {
    luPickerPaint(picker);
  } else {
    var add = panel.querySelector('[data-add]');
    var label = add && add.querySelector('[data-add-label]');
    if (add && label && /^Choose /.test(label.textContent)) {
      add.disabled = false;
      label.textContent = 'Add to cart';
    }
  }
}

document.addEventListener('click', function (e) {
  var chip = e.target.closest('[data-picker-chip]');
  if (chip) {
    if (chip.disabled) return;
    chip.setAttribute('aria-pressed', String(chip.getAttribute('aria-pressed') !== 'true'));
    luPickerPaint(chip.closest('[data-picker]'));
    return;
  }
  var clear = e.target.closest('[data-picker-clear]');
  if (clear) {
    var picker = clear.closest('[data-picker]');
    picker.querySelectorAll('[data-picker-chip]').forEach(function (c) {
      c.setAttribute('aria-pressed', 'false');
    });
    luPickerPaint(picker);
  }
});

/* First paint: the variant may already be the build-your-own one, from a
   ?variant= link or a reload. */
document.querySelectorAll('[data-picker]').forEach(function (picker) {
  var panel = picker.closest('[data-buy]');
  var data = panel && panel.querySelector('[data-variants]');
  var id = panel && panel.querySelector('[data-variant-id]');
  if (!data || !id) return;
  var current = JSON.parse(data.textContent).find(function (v) {
    return String(v.id) === String(id.value);
  });
  if (current) luSyncPicker(panel, current.options);
});

/* The engraved message. Same shape as the picker: the variant charges for it,
   the words travel as a line item property, and the input stays disabled --
   and therefore unsubmitted -- until the option that pays for it is chosen. */
function luSyncMessage(panel, options) {
  var box = panel && panel.querySelector('[data-message]');
  if (!box) return;
  var on = Array.isArray(options) && options.indexOf(box.dataset.messageFor) !== -1;
  var input = box.querySelector('[data-message-input]');

  box.hidden = !on;
  if (input) {
    input.disabled = !on;
    if (!on) input.value = '';
  }
  luMessagePaint(box);
}

function luMessagePaint(box) {
  var input = box.querySelector('[data-message-input]');
  var count = box.querySelector('[data-message-count]');
  if (!input) return;
  var max = input.getAttribute('maxlength') || '';
  if (count) count.textContent = input.value.length + ' / ' + max;

  /* An engraved card with nothing on it is a support ticket, not an order, so
     an empty message holds the button the same way an incomplete picker does.
     Whichever gate is unsatisfied wins, and stock wins over both. */
  var panel = box.closest('[data-buy]');
  var add = panel && panel.querySelector('[data-add]');
  if (!add || box.hidden) return;
  var label = add.querySelector('[data-add-label]');
  var picker = panel.querySelector('[data-picker]');
  var pickerShort = false;
  if (picker && !picker.hidden) {
    var limit = Number(picker.dataset.pickerLimit) || 25;
    pickerShort = luPickerState(picker).length < limit;
  }
  var sellable = luVariantAvailable(panel);
  var empty = input.value.trim().length === 0;

  add.disabled = !sellable || pickerShort || empty;
  if (label) {
    label.textContent = !sellable ? 'Sold out'
      : pickerShort ? label.textContent
      : empty ? 'Add your message'
      : 'Add to cart';
  }
}

document.addEventListener('input', function (e) {
  var input = e.target.closest('[data-message-input]');
  if (input) luMessagePaint(input.closest('[data-message]'));
});

document.querySelectorAll('[data-message]').forEach(function (box) {
  var panel = box.closest('[data-buy]');
  var data = panel && panel.querySelector('[data-variants]');
  var id = panel && panel.querySelector('[data-variant-id]');
  if (!data || !id) return;
  var current = JSON.parse(data.textContent).find(function (v) {
    return String(v.id) === String(id.value);
  });
  if (current) luSyncMessage(panel, current.options);
});


/* ------------------------------------------------------------------ share --
   Behaviour for snippets/lucullion-share.liquid.

   The sheet is detected here rather than in Liquid: navigator.share is
   undefined during any server render, and a value read there would stick. */
(function () {
  var shares = document.querySelectorAll('[data-share]');
  if (!shares.length) return;

  var hasSheet = typeof navigator !== 'undefined' && !!navigator.share;

  shares.forEach(function (root) {
    var toggle = root.querySelector('[data-share-toggle]');
    var sheetBtn = root.querySelector('[data-share-sheet]');
    var menu = root.querySelector('[data-share-menu]');
    var copyBtn = root.querySelector('[data-share-copy]');
    var copyLabel = root.querySelector('[data-share-copy-label]');
    var manual = root.querySelector('[data-share-manual]');
    var field = root.querySelector('[data-share-field]');
    var status = root.querySelector('[data-share-status]');

    var pageUrl = field ? field.value : window.location.href;
    var copyTimer = null;

    /* Inert without script, so it ships hidden and is revealed here. A button
       that does nothing is worse than no button. */
    root.hidden = false;

    /* The sheet is a row in the menu, not a second trigger. Revealed only
       where the device has one; the explicit channels stay a tap away either
       way, which is why one button is enough. */
    if (hasSheet && sheetBtn) sheetBtn.hidden = false;

    function close() {
      if (!menu) return;
      menu.hidden = true;
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    function open() {
      if (!menu) return;
      menu.hidden = false;
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    }

    if (toggle) {
      toggle.addEventListener('click', function () {
        if (menu.hidden) { open(); } else { close(); }
      });
    }

    if (sheetBtn) {
      sheetBtn.addEventListener('click', function () {
        var title = document.title.split(/\s+[\u2014\u2013|]\s+/)[0].trim() || document.title;
        /* Rejects when the customer dismisses the sheet. A normal outcome. */
        navigator.share({ title: title, text: title, url: pageUrl }).catch(function () {});
        close();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu && !menu.hidden) {
        close();
        if (toggle) toggle.focus();
      }
    });

    document.addEventListener('mousedown', function (e) {
      if (menu && !menu.hidden && !root.contains(e.target)) close();
    });

    function copyViaField() {
      var el = document.createElement('textarea');
      el.value = pageUrl;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (err) { ok = false; }
      document.body.removeChild(el);
      return ok;
    }

    function done() {
      if (copyLabel) copyLabel.textContent = 'Link copied';
      if (status) status.textContent = 'Link copied';
      if (manual) manual.hidden = true;
      clearTimeout(copyTimer);
      copyTimer = setTimeout(function () {
        if (copyLabel) copyLabel.textContent = 'Copy link';
        if (status) status.textContent = '';
      }, 2400);
    }

    /* A press must never do nothing. If both routes fail, show the URL. */
    function failed() {
      if (copyLabel) copyLabel.textContent = 'Select the link below';
      if (manual) manual.hidden = false;
      if (status) status.textContent = 'Copying was blocked. Select the link below.';
      if (field) { field.focus(); field.select(); }
    }

    if (field) {
      field.addEventListener('focus', function () { field.select(); });
    }

    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        /* writeText rejects with NotAllowedError on an unfocused document, a
           denied permission, or a cross-origin frame -- the commonest real
           failure. Wiring the fallback only to a missing clipboard object
           leaves it unreachable in exactly the case it exists for, and an
           empty catch makes the button silently dead. */
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(pageUrl).then(done).catch(function () {
            if (copyViaField()) { done(); } else { failed(); }
          });
        } else if (copyViaField()) {
          done();
        } else {
          failed();
        }
      });
    }
  });
})();
